// useResumeState — Plan-1.9 Phase 3 Sub-Phase 3.A.
//
// localStorage-basierter Resume-State pro Scrolly (publicKey).
// Privacy + Simplicity: kein Backend, alles im Browser. Cross-Device-
// Resume ist Plan-2-Backlog-Item (Master-Briefing v5 Z. 553).
//
// Storage-Schema:
//   Key:   scrolly:resume:{publicKey}
//   Value: { scrollPositionPercent, lastVisit, scrollyVersion }
//
// Lifecycle:
//   - Mount: Cleanup-Pass (löscht Einträge älter als 30 Tage), dann
//            Read für den aktuellen publicKey.
//   - save(): merkt sich aktuelle Position + scrollyVersion.
//   - clear(): löscht Eintrag für den aktuellen publicKey.
//
// SSR-Verhalten: Default-State = null, useEffect setzt erst client-
// seitig den geladenen State. Vermeidet Hydration-Mismatch (Pattern
// analog useViewerMode.ts).
//
// Storage-Failures (Safari Private Mode, Quota-Exceeded): try/catch,
// stiller Fallback auf no-op. Resume ist nicht-kritisch, der Renderer
// funktioniert auch ohne.

import { useCallback, useEffect, useState } from "react";

export interface ResumeState {
  /** 0..1 — Position als Anteil des max-scroll. */
  scrollPositionPercent: number;
  /** ISO-String — letzter Visit-Zeitpunkt. */
  lastVisit: string;
  /** scrolly.updatedAt ISO-String — invalidiert Resume bei Inhalt-Update. */
  scrollyVersion: string;
}

const KEY_PREFIX = "scrolly:resume:";
const TTL_DAYS = 30;
const TTL_MS = TTL_DAYS * 24 * 60 * 60 * 1000;

function buildKey(publicKey: string): string {
  return `${KEY_PREFIX}${publicKey}`;
}

function isResumeState(value: unknown): value is ResumeState {
  if (!value || typeof value !== "object") return false;
  const v = value as Partial<ResumeState>;
  return (
    typeof v.scrollPositionPercent === "number" &&
    Number.isFinite(v.scrollPositionPercent) &&
    v.scrollPositionPercent >= 0 &&
    v.scrollPositionPercent <= 1 &&
    typeof v.lastVisit === "string" &&
    typeof v.scrollyVersion === "string"
  );
}

/**
 * Liest den Resume-State für einen Scrolly. Liefert null bei:
 *  - SSR (kein window)
 *  - Storage disabled (Safari Private Mode etc.)
 *  - Kein Eintrag
 *  - Eintrag korrupt / falscher Shape
 *
 * Pure function — testbar ohne React-Setup.
 */
export function readResumeState(publicKey: string): ResumeState | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(buildKey(publicKey));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return isResumeState(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

/**
 * Schreibt einen Resume-State. Schluckt Quota-Errors stillschweigend
 * (Resume ist nicht-kritisch). scrollPositionPercent wird auf [0,1]
 * geclampt.
 */
export function writeResumeState(
  publicKey: string,
  state: ResumeState,
): void {
  if (typeof window === "undefined") return;
  try {
    const clamped: ResumeState = {
      ...state,
      scrollPositionPercent: Math.max(
        0,
        Math.min(1, state.scrollPositionPercent),
      ),
    };
    window.localStorage.setItem(buildKey(publicKey), JSON.stringify(clamped));
  } catch {
    // Quota voll / Storage disabled — Resume entfällt für diesen Visit
  }
}

/**
 * Löscht den Resume-State für einen Scrolly. Idempotent, kein Error
 * wenn Eintrag fehlt.
 */
export function clearResumeState(publicKey: string): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.removeItem(buildKey(publicKey));
  } catch {
    // Storage disabled — no-op
  }
}

/**
 * UI-Helper für Resume-Toast: Approximation für "Weiterlesen ab Slide X".
 * Liefert 1-based slideIndex aus scroll-percent (0..1) und totalSlides.
 * Geklemmt auf [1, totalSlides]. Bei fehlendem totalSlides oder
 * non-finite percent: undefined — Consumer-UI fällt auf Percent-Display
 * zurück ("Du warst bei N%").
 *
 * Approximation, nicht exakt: scroll-percent → slide-Number ist
 * Renderer-Wissen (slidePositions in VisualStoryStore). Diese Funktion
 * nutzt einfache lineare Verteilung, was bei ~gleich-langen Slides
 * treffsicher ist und bei stark unterschiedlichen Längen ungenau wird.
 * UX-Hint, nicht Source-of-Truth — der Resume-Restore selbst nutzt
 * exact percent (via scrollTop = scrollHeight × percent), nicht slideIndex.
 *
 * Plan-2-Backlog: Renderer-API-Erweiterung für genaue Slide-Berechnung
 * (siehe docs/PHASE-3-PRE-VERIFIKATION-2026-05-10.md).
 */
export function approximateSlideIndex(
  percent: number,
  totalSlides: number | undefined,
): number | undefined {
  if (typeof totalSlides !== "number" || totalSlides <= 0) return undefined;
  if (typeof percent !== "number" || !Number.isFinite(percent)) return undefined;
  return Math.max(
    1,
    Math.min(totalSlides, Math.round(percent * totalSlides)),
  );
}

/**
 * Räumt alle `scrolly:resume:*`-Einträge auf, deren lastVisit älter als
 * TTL_DAYS (30 Tage) ist. Außerdem werden korrupte Einträge entfernt
 * (broken JSON, falscher Shape, unparseable Date). Liefert die Anzahl
 * entfernter Einträge (für Tests + Debug).
 *
 * `now` ist injizierbar für deterministische Tests.
 */
export function cleanupExpiredEntries(now: Date = new Date()): number {
  if (typeof window === "undefined") return 0;
  let removed = 0;
  try {
    const storage = window.localStorage;
    const cutoff = now.getTime() - TTL_MS;
    const keysToRemove: string[] = [];
    for (let i = 0; i < storage.length; i++) {
      const key = storage.key(i);
      if (!key || !key.startsWith(KEY_PREFIX)) continue;
      try {
        const raw = storage.getItem(key);
        if (!raw) continue;
        const parsed = JSON.parse(raw);
        if (!isResumeState(parsed)) {
          keysToRemove.push(key); // korrupter Shape → entfernen
          continue;
        }
        const visitMs = Date.parse(parsed.lastVisit);
        if (Number.isNaN(visitMs) || visitMs < cutoff) {
          keysToRemove.push(key);
        }
      } catch {
        keysToRemove.push(key); // broken JSON → entfernen
      }
    }
    for (const key of keysToRemove) {
      storage.removeItem(key);
      removed += 1;
    }
  } catch {
    // Storage komplett kaputt — kein Cleanup, kein Crash
  }
  return removed;
}

export interface UseResumeStateReturn {
  /**
   * Aktueller gespeicherter State, oder null wenn kein Eintrag /
   * Storage disabled / SSR / Mount noch nicht durch.
   *
   * Versionsprüfung (scrolly.updatedAt vs. state.scrollyVersion) macht
   * der Consumer (Resume-Toast in CP2) — der Hook liefert den State
   * unverändert, damit Consumer sowohl "weiterlesen" als auch
   * "Inhalt-aktualisiert"-Pfade kennen kann.
   */
  state: ResumeState | null;
  /** Speichert die aktuelle Scroll-Position. */
  save: (scrollPositionPercent: number, scrollyVersion: string) => void;
  /** Löscht den Eintrag (z.B. nach Resume-Click "Neu starten"). */
  clear: () => void;
}

/**
 * React-Hook-Wrapper um read/write/clear + Mount-Cleanup. Mountet beim
 * ersten Render mit `state = null` (SSR-safe), läuft im useEffect den
 * Cleanup-Pass und liest den initialen State für `publicKey`.
 */
export function useResumeState(publicKey: string): UseResumeStateReturn {
  const [state, setState] = useState<ResumeState | null>(null);

  useEffect(() => {
    if (!publicKey) return;
    cleanupExpiredEntries();
    setState(readResumeState(publicKey));
  }, [publicKey]);

  const save = useCallback(
    (scrollPositionPercent: number, scrollyVersion: string) => {
      if (!publicKey) return;
      const next: ResumeState = {
        scrollPositionPercent: Math.max(0, Math.min(1, scrollPositionPercent)),
        lastVisit: new Date().toISOString(),
        scrollyVersion,
      };
      writeResumeState(publicKey, next);
      setState(next);
    },
    [publicKey],
  );

  const clear = useCallback(() => {
    if (!publicKey) return;
    clearResumeState(publicKey);
    setState(null);
  }, [publicKey]);

  return { state, save, clear };
}
